import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerAPI } from '../../services/api';
import CustomerDetail from './CustomerDetail';
import CustomerForm from './CustomerForm';
import './Customers.css';

const CustomerListPage = ({ user }) => {
  const navigate = useNavigate();
  const { action, id } = useParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, search, sortBy, sortOrder]);

  // Handle URL-based modal opening
  useEffect(() => {
    if (action === 'new') {
      setIsEditMode(false);
      setSelectedCustomerId(null);
      setShowFormModal(true);
    } else if (action === 'edit' && id) {
      setIsEditMode(true);
      setSelectedCustomerId(id);
      setShowFormModal(true);
    } else if (action === 'view' && id) {
      setSelectedCustomerId(id);
      setShowDetailModal(true);
    } else {
      setShowDetailModal(false);
      setShowFormModal(false);
    }
  }, [action, id]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        per_page: 15,
      };
      const response = await customerAPI.getAll(params);
      setCustomers(response.data);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        total: response.total,
        per_page: response.per_page,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCustomers();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await customerAPI.delete(customerId);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete customer');
    }
  };

  const handleView = (customerId) => {
    navigate(`/customers/view/${customerId}`);
  };

  const handleEdit = (customerId) => {
    navigate(`/customers/edit/${customerId}`);
  };

  const handleNew = () => {
    navigate('/customers/new');
  };

  const closeModal = () => {
    navigate('/customers');
    setShowDetailModal(false);
    setShowFormModal(false);
    setSelectedCustomerId(null);
  };

  const handleFormSuccess = () => {
    closeModal();
    fetchCustomers();
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Customers</h1>
              <button 
                className="btn btn-primary"
                onClick={handleNew}
              >
                + Add Customer
              </button>
            </div>

            <div className="customer-search">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="btn btn-secondary">
                  Search
                </button>
                {search && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSearch('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading && customers.length === 0 ? (
              <div className="loading">Loading customers...</div>
            ) : (
              <>
                <div className="customer-table-container">
                  <table className="customer-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('fullname')} className="sortable">
                          Name {getSortIcon('fullname')}
                        </th>
                        <th onClick={() => handleSort('phone')} className="sortable">
                          Phone {getSortIcon('phone')}
                        </th>
                        <th onClick={() => handleSort('email')} className="sortable">
                          Email {getSortIcon('email')}
                        </th>
                        <th>Delivery Address</th>
                        <th>Trailer Access</th>
                        <th onClick={() => handleSort('created_at')} className="sortable">
                          Created {getSortIcon('created_at')}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="no-data">
                            No customers found
                          </td>
                        </tr>
                      ) : (
                        customers.map((customer) => (
                          <tr key={customer.id}>
                            <td>{customer.fullname}</td>
                            <td>{customer.phone}</td>
                            <td>{customer.email || '-'}</td>
                            <td className="address-cell">
                              {customer.delivery_address || '-'}
                            </td>
                            <td>
                              <span className={`badge ${customer.trailer_access ? 'badge-success' : 'badge-secondary'}`}>
                                {customer.trailer_access ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                            <td className="actions-cell">
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleView(customer.id)}
                              >
                                View
                              </button>
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleEdit(customer.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(customer.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination && pagination.last_page > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {pagination.current_page} of {pagination.last_page} 
                      ({pagination.total} total)
                    </span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.last_page}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCustomerId && (
        <CustomerDetail 
          customerId={selectedCustomerId}
          onClose={closeModal}
          onEdit={() => handleEdit(selectedCustomerId)}
          onDelete={async () => {
            await handleDelete(selectedCustomerId);
            closeModal();
          }}
        />
      )}

      {/* Form Modal */}
      {showFormModal && (
        <CustomerForm
          customerId={isEditMode ? selectedCustomerId : null}
          onClose={closeModal}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default CustomerListPage;
